import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Home, Inbox, Calendar as CalendarIcon, ListChecks, Settings, LogOut } from "lucide-react";

// Same five tabs as the iPhone app: along the top on a computer, along the bottom on a phone.
const TABS = [
  { to: "/", label: "Family", icon: Home, match: (p) => p === "/" || p.startsWith("/kids/") || p === "/times" },
  { to: "/calendar", label: "Calendar", icon: CalendarIcon },
  { to: "/approvals", label: "Requests", icon: Inbox, badge: true },
  { to: "/tasks", label: "Jobs", icon: ListChecks },
  { to: "/family", label: "Settings", icon: Settings },
];

/** Requests still waiting on a parent (asks from kids plus cash-outs), for the badge on the Requests tab. */
function useWaitingCount(pathname) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [a, c] = await Promise.all([
          api.get("/approvals?status=pending"),
          api.get("/cashout-requests?status=pending"),
        ]);
        if (alive) setN((a.data?.length || 0) + (c.data?.length || 0));
      } catch (_) { /* the badge is a nicety; leave it as it was */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [pathname]);
  return n;
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const waiting = useWaitingCount(location.pathname);

  const isOn = (t) => (t.match ? t.match(location.pathname) : location.pathname === t.to);
  const slug = (t) => t.label.toLowerCase();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#F5F4F0]">
      <header className="sticky top-0 z-30 border-b border-[#E4E2DC] bg-[#F5F4F0]/90 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5" data-testid="brand-link">
            <img src="/img/yoda-mascot.png" alt="" className="h-9 w-9 rounded-xl object-cover bg-[#E6EDE1]" />
            <span className="text-[17px] font-bold tracking-tight text-[#2C342A]">Yoda Watch</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1" data-testid="top-nav">
            {TABS.map((t) => {
              const Icon = t.icon;
              const on = isOn(t);
              return (
                <Link key={t.to} to={t.to} data-testid={`nav-${slug(t)}`}
                      className={`relative inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold transition-colors ${
                        on ? "bg-white text-[#5C7A4F] shadow-[0_1px_3px_rgba(0,0,0,0.06)]" : "text-[#707A6E] hover:text-[#2C342A]"}`}>
                  <Icon size={17} /> {t.label}
                  {t.badge && waiting > 0 && (
                    <span className="ml-0.5 rounded-full bg-[#C06E52] px-1.5 text-[11px] font-bold leading-[18px] text-white">{waiting}</span>
                  )}
                </Link>
              );
            })}
          </nav>

          {user && (
            <div className="flex items-center gap-2 text-sm">
              <span className="hidden lg:inline text-[#707A6E]" data-testid="user-name">{user.name || user.email}</span>
              <button data-testid="logout-btn" onClick={handleLogout} title="Sign out"
                      className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 font-medium text-[#707A6E] hover:bg-[#F0EFEB] hover:text-[#2C342A]">
                <LogOut size={16} /> <span className="hidden sm:inline">Sign out</span>
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-8 pt-6 pb-28 md:py-10 fade-in-up">{children}</main>

      {/* Phone: the app's tab bar along the bottom. */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 border-t border-[#E4E2DC] bg-white/95 backdrop-blur pb-[env(safe-area-inset-bottom)]"
           data-testid="mobile-nav">
        <div className="flex justify-around pt-2 pb-2">
          {TABS.map((t) => {
            const Icon = t.icon;
            const on = isOn(t);
            return (
              <Link key={t.to} to={t.to} data-testid={`mobile-nav-${slug(t)}`}
                    className={`relative flex w-16 flex-col items-center gap-0.5 text-[10.5px] font-semibold ${on ? "text-[#5C7A4F]" : "text-[#9AA096]"}`}>
                <Icon size={24} strokeWidth={on ? 2.4 : 2} />
                {t.label}
                {t.badge && waiting > 0 && (
                  <span className="absolute -top-1 right-3 rounded-full bg-[#C06E52] px-1.5 text-[10px] font-bold leading-4 text-white">{waiting}</span>
                )}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
